import React, { useState } from 'react';
import { calculateTextDiff, renderDiffAsReactElements, extractChangesFromDiff } from '../utils/textDiff';

const CorrectionResult = ({ data, mode, originalText, isCached }) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const [showAllChanges, setShowAllChanges] = useState(false);
  
  console.log('[CorrectionResult] 컴포넌트 렌더링 시작:', { data, mode, originalText, isCached });
  
  if (!data) {
    console.log('[CorrectionResult] 데이터 없음');
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <div className="text-red-500">결과 데이터가 없습니다.</div>
      </div>
    );
  }

  try {
    // 디버깅: API 응답 구조 확인
    console.log('[CorrectionResult] 받은 데이터:', data);
    console.log('[CorrectionResult] 데이터 타입:', typeof data);
    console.log('[CorrectionResult] 데이터 키들:', Object.keys(data));
    console.log('[CorrectionResult] 원본 텍스트 (prop):', originalText);

    // API 응답에서 데이터 추출 (여러 가능한 구조에 대응)
    let original, revised, reasons, apiChanges;
    
    // 경우 1: 직접적인 구조 { original, revised, ... }
    if (data.original && data.revised) {
      original = data.original;
      revised = data.revised;
      reasons = data.reasons;
      apiChanges = data.changes;
    }
    // 경우 2: result 안에 있는 구조 { result: { original, revised }, ... }
    else if (data.result && data.result.original && data.result.revised) {
      original = data.result.original;
      revised = data.result.revised;
      reasons = data.result.reasons || data.reasons;
      apiChanges = data.result.changes || data.changes;
    }
    // 경우 3: 다른 필드명 사용 (text, corrected_text 등)
    else if (data.text && data.corrected_text) {
      original = data.text;
      revised = data.corrected_text;
      reasons = data.reasons;
      apiChanges = data.changes;
    }
    // 경우 4: revised만 있고 original은 prop으로 전달받은 경우
    else if (data.revised || data.corrected_text || data.result) {
      original = originalText;
      revised = data.revised || data.corrected_text || data.result || data.text;
      reasons = data.reasons;
      apiChanges = data.changes;
    }
    // 경우 5: 전체 응답이 텍스트인 경우 (fallback)
    else if (typeof data === 'string') {
      original = originalText;
      revised = data;
      reasons = null;
      apiChanges = null;
    }
    // 경우 6: originalText prop 사용 (마지막 fallback)
    else {
      original = originalText;
      revised = originalText; // 변경사항이 없는 경우
      reasons = data.reasons;
      apiChanges = data.changes;
    }
    
    // 디버깅: 추출된 값들 확인
    console.log('[CorrectionResult] 최종 original:', original);
    console.log('[CorrectionResult] 최종 revised:', revised);
    console.log('[CorrectionResult] 최종 reasons:', reasons);
    console.log('[CorrectionResult] API Changes:', apiChanges);
    
    // 안전장치: 텍스트가 없는 경우
    if (!original && !revised) {
      console.log('[CorrectionResult] 텍스트 데이터 없음');
      return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="text-yellow-600">표시할 텍스트가 없습니다.</div>
          <div className="text-sm text-gray-500 mt-2">받은 데이터: {JSON.stringify(data)}</div>
        </div>
      );
    }
    
    // 텍스트 차이점 계산
    const diffResult = calculateTextDiff(original, revised);
    console.log('[CorrectionResult] diffResult:', diffResult);
    
    // 변경사항 추출 (수정 요약용)
    let changes;
    if (apiChanges && apiChanges.length > 0) {
      // API에서 changes 정보가 있는 경우 - 해당 정보를 사용
      console.log('[CorrectionResult] API Changes 사용');
      changes = apiChanges.map(change => ({
        original: change.original || change.before || change.from,
        revised: change.corrected || change.after || change.to,
        reason: change.reason || change.message,
        type: 'content'
      }));
    } else {
      // API에서 changes 정보가 없는 경우 - diff 비교 사용하되 띄어쓰기 조정 제거
      console.log('[CorrectionResult] Diff 비교 사용 (띄어쓰기 조정 제거)');
      const allChanges = extractChangesFromDiff(diffResult, original, revised);
      changes = allChanges.filter(change => change.type !== 'whitespace-group' && change.type !== 'whitespace');
    }
    console.log('[CorrectionResult] 최종 변경사항:', changes);
    
    const hasChanges = diffResult.some(item => item.changed);

    // 모드별 제목
    const modeTitle = mode === 'proofread' ? '교정 결과' : '교열 결과';
    const modeColor = mode === 'proofread' ? 'blue' : 'green';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <span className="material-icons text-2xl" style={{ color: mode === 'proofread' ? '#4A90E2' : '#7ED37E' }}>
            {mode === 'proofread' ? 'spellcheck' : 'edit_note'}
          </span>
          <h3 className="text-lg font-semibold text-gray-800">{modeTitle}</h3>
          {isCached && (
            <div className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">
              <span className="material-icons text-sm">cached</span>
              <span>캐시됨</span>
            </div>
          )}
        </div>
        
        {/* 원본/결과 토글 버튼 */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowOriginal(!showOriginal)}
            className="text-sm px-3 py-1 rounded-full border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            {showOriginal ? '결과 보기' : '원본 보기'}
          </button>
          
          {/* 복사 버튼 */}
          <button
            onClick={() => navigator.clipboard.writeText(revised)}
            className="text-sm px-3 py-1 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
            title="결과 복사"
          >
            <span className="material-icons text-sm">content_copy</span>
          </button>
        </div>
      </div>

      {/* 캐시된 결과 안내 메시지 */}
      {isCached && (
        <div className="mb-4 p-3 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
          <div className="flex items-center space-x-2">
            <span className="material-icons text-green-600">info</span>
            <div className="text-sm text-green-800">
              <span className="font-medium">크레딧 차감 없음</span>
              <span className="text-green-600 ml-1">- 이전에 처리한 동일한 내용으로 캐시된 결과를 사용했습니다</span>
            </div>
          </div>
        </div>
      )}

      {/* 결과 텍스트 영역 */}
      <div className="mb-4">
        {showOriginal ? (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-2 font-medium">원본 텍스트</div>
            <div className="text-gray-800 whitespace-pre-wrap leading-relaxed">
              {original}
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-2 font-medium">
              수정된 텍스트 
              {hasChanges && <span className="text-yellow-600">(노란색: 수정된 부분)</span>}
            </div>
            <div className="text-gray-800 whitespace-pre-wrap leading-relaxed">
              {renderDiffAsReactElements(diffResult)}
            </div>
          </div>
        )}
      </div>

      {/* 수정 요약 */}
      {hasChanges && (
        <div className="border-t pt-4">
          <div className="flex items-center space-x-2 mb-3">
            <span className="material-icons text-lg text-gray-600">fact_check</span>
            <span className="text-sm font-medium text-gray-700">수정 내용</span>
          </div>
          
          {changes && changes.length > 0 ? (
            <div className="space-y-2">
              {changes.slice(0, showAllChanges ? changes.length : 5).map((change, index) => (
                <div key={index} className="text-sm bg-blue-50 p-3 rounded border-l-4 border-blue-200">
                  <div className="text-gray-700">
                    {change.type === 'whitespace-group' ? (
                      <span className="font-medium text-purple-600">
                        띄어쓰기 조정 {change.count}개
                      </span>
                    ) : (
                      <>
                        <div>
                          <span className="line-through text-red-600">{change.original}</span>
                          {' → '}
                          <span className="font-medium text-green-600">{change.revised}</span>
                        </div>
                        {change.reason && (
                          <div className="mt-1 text-xs text-gray-600">
                            {change.reason}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
              {changes.length > 5 && (
                <div className="text-center">
                  <button
                    onClick={() => setShowAllChanges(!showAllChanges)}
                    className="text-sm text-blue-600 hover:text-blue-800 underline"
                  >
                    {showAllChanges ? 
                      '간략히 보기' : 
                      `+${changes.length - 5}개 더 많은 수정사항 보기`
                    }
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-600 bg-green-50 p-3 rounded border-l-4 border-green-200">
              <span className="flex items-center space-x-2">
                <span className="material-icons text-green-600 text-sm">check_circle</span>
                <span>수정할 부분이 발견되지 않았습니다.</span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* 수정 이유 (includereasons=true인 경우) */}
      {reasons && reasons.length > 0 && (
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center space-x-2 mb-3">
            <span className="material-icons text-lg text-gray-600">help_outline</span>
            <span className="text-sm font-medium text-gray-700">수정 이유</span>
          </div>
          <div className="space-y-2">
            {reasons.slice(0, 3).map((reason, index) => (
              <div key={index} className="text-sm bg-yellow-50 p-3 rounded border-l-4 border-yellow-200">
                <div className="text-gray-700">{reason}</div>
              </div>
            ))}
            {reasons.length > 3 && (
              <div className="text-xs text-gray-500 text-center">
                +{reasons.length - 3}개 더 많은 설명
              </div>
            )}
          </div>
        </div>
      )}


    </div>
  );
  
  } catch (error) {
    console.error('[CorrectionResult] 렌더링 오류:', error);
    return (
      <div className="bg-white border border-red-200 rounded-lg p-6 shadow-sm">
        <div className="text-red-600 font-semibold mb-2">결과 표시 중 오류가 발생했습니다</div>
        <div className="text-sm text-gray-600 mb-3">오류: {error.message}</div>
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <div>받은 데이터:</div>
          <pre>{JSON.stringify(data, null, 2)}</pre>
        </div>
      </div>
    );
  }
};

export default CorrectionResult; 